import { AfterViewInit, Component, Input, ViewChild } from '@angular/core';
import { TERMS_AND_CONDITIONS } from '@environments';
import { NavbarItemLink } from '@portal/shared/core/src/index';
import { BaseModalComponent } from '@portal/shared/ui/modal/src';
import { CookiePolicyContentComponent } from './cookie-policy-content/cookie-policy-content.component';

declare global {
  interface Window {
    Cookiebot?: {
      renew?: () => void;
      show?: () => void;
      withdraw?: () => void;
      hasResponse?: boolean;
      consented?: boolean;
      consentID?: string | number;
      consentUTC?: string;
      domain?: string;
      consent?: {
        stamp?: string;
        necessary?: boolean;
        preferences?: boolean;
        statistics?: boolean;
        marketing?: boolean;
        method?: string;
      };
    };
  }
}

@Component({
    selector: 'vui-footer',
    templateUrl: './footer.component.html',
    styleUrls: ['./footer.component.scss'],
    standalone: false
})
export class FooterComponent implements AfterViewInit {
  @Input() isPetroUser = false;
  @Input() isLoggedIn = false;
  @Input() supportLink: NavbarItemLink | null = null;
  @ViewChild('cookiePolicyModal') cookiePolicyModal: BaseModalComponent;
  @ViewChild('cookiePolicyContent') cookiePolicyContent: CookiePolicyContentComponent;
  termsConditionsLink = TERMS_AND_CONDITIONS.TC_URL;
  privacyPolicyLink = TERMS_AND_CONDITIONS.PP_URL;
  legalLink = TERMS_AND_CONDITIONS.LEGAL_URL;
  documentationLink = TERMS_AND_CONDITIONS.DOCUMENTATION_URL;
  apiRefLink = TERMS_AND_CONDITIONS.API_REF;
  cookieDeclarationUrl = this.resolveCookieDeclarationUrl();
  currentYear = new Date().getFullYear();
  isAuthenticationApp = this.checkIsAuthenticationApp();

  /**
   * Detects whether the footer is being rendered by the standalone
   * authentication app (UIS FE). The authentication app is served on a
   * different origin and always carries a `redirect_uri` query param pointing
   * back to the portal, so its presence is used as the URL-based signal.
   */
  private checkIsAuthenticationApp(): boolean {
    return new URLSearchParams(window.location.search).has('redirect_uri');
  }

  /**
   * Resolves the Cookie Declaration page URL. The page lives in the wrapper
   * (portal) app, so when the footer is rendered by an app served on a
   * different origin (e.g. the authentication app), we derive the portal
   * origin from the `redirect_uri` query param. Falls back to the current
   * origin (the wrapper case) when the param is absent or malformed.
   */
  private resolveCookieDeclarationUrl(): string {
    let origin = window.location.origin;
    const redirectUri = new URLSearchParams(window.location.search).get('redirect_uri');
    if (redirectUri) {
      try {
        origin = new URL(redirectUri).origin;
      } catch {
        // Malformed redirect_uri — fall back to the current origin.
      }
    }
    return `${origin}/cookie-declaration`;
  }

  /**
   * The Cookie Policy content component is not recreated when the modal
   * opens/closes, so its consent details would otherwise stay frozen at the
   * value captured in `ngOnInit`. Refresh them whenever the modal closes so the
   * next open (and the current view) reflects the user's latest choice.
   */
  ngAfterViewInit(): void {
    this.cookiePolicyModal?.closeModal.subscribe(() => {
      this.cookiePolicyContent?.refreshConsentDetails();
    });
  }

  openCookiePolicyModal(): void {
    this.cookiePolicyContent?.refreshConsentDetails();
    this.cookiePolicyModal?.open();
  }

  /**
   * Closes the Cookie Policy modal and opens the existing Cookiebot consent
   * dialog so the user can view/change their current cookie preferences.
   * Safe no-op if Cookiebot is not present.
   */
  openCookiePreferences(): void {
    this.cookiePolicyModal?.close();
    if (window.Cookiebot?.renew) {
      window.Cookiebot.renew();
    } else if (window.Cookiebot?.show) {
      window.Cookiebot.show();
    }
  }
}
